import React, { useState, useMemo } from 'react';
import { DUMMY_BUMDES, DUMMY_PO_LIST } from './mocks/dplDummyData';

const BMCStatusBadge = ({ status }) => {
  const map = {
    Disetujui: {
      color: 'success',
      icon: 'bi-check-circle-fill',
      label: 'DISETUJUI',
    },
    Direvisi: {
      color: 'warning',
      icon: 'bi-exclamation-triangle-fill',
      label: 'DIREVISI',
    },
    Draft: { color: 'secondary', icon: 'bi-pencil-fill', label: 'DRAFT' },
    APPROVED_BY_DPL: {
      color: 'info',
      icon: 'bi-clipboard-check-fill',
      label: 'WAITING ADMIN',
    },
  };
  const config = map[status] || {
    color: 'secondary',
    icon: 'bi-dot',
    label: status,
  };
  return (
    <span
      className={`badge bg-${config.color} bg-opacity-10 text-${config.color} border border-${config.color} border-opacity-25 rounded-pill px-3 py-1 extra-small fw-bold d-inline-flex align-items-center gap-1`}
    >
      <i className={`bi ${config.icon}`}></i> {config.label}
    </span>
  );
};

const MonitoringBMC = () => {
  const bmc = DUMMY_BUMDES.bmc;
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('all');

  const filteredHistory = useMemo(() => {
    let list = bmc.history;
    if (query) {
      list = list.filter(
        (h) =>
          h.updatedBy.toLowerCase().includes(query.toLowerCase()) ||
          h.notes.toLowerCase().includes(query.toLowerCase()),
      );
    }
    if (filter !== 'all') {
      list = list.filter((h) => h.status === filter);
    }
    return list;
  }, [query, filter, bmc.history]);

  // Unique POs who contributed
  const totalContributors = useMemo(() => {
    const contributors = bmc.history.map((h) => h.updatedBy.split(' (')[0]);
    return [...new Set(contributors)].length;
  }, [bmc.history]);

  return (
    <div className='card card-body border-0 shadow-sm rounded-4 mb-5'>
      {/* ── Page Header ── */}
      <div className='d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3 mb-4'>
        <div className='d-flex align-items-center gap-3'>
          <div
            className='bg-primary bg-opacity-10 d-flex align-items-center justify-content-center rounded-3'
            style={{ width: 40, height: 40 }}
          >
            <i className='bi bi-kanban-fill text-primary fs-5'></i>
          </div>
          <div>
            <h1 className='h5 fw-bold mb-0 text-dark'>
              Monitoring Business Model Canvas (BMC)
            </h1>
            <p
              className='text-muted mb-0 d-none d-lg-block'
              style={{ fontSize: '0.75rem' }}
            >
              Pantau pengerjaan kolaboratif BMC{' '}
              <strong>{DUMMY_BUMDES.name}</strong> secara bergiliran oleh tim
              Project Officer.
            </p>
          </div>
        </div>
        <span
          className='badge bg-primary bg-opacity-10 text-primary px-3 py-2 rounded-pill fw-semibold align-self-start'
          style={{ fontSize: '0.75rem' }}
        >
          <i className='bi bi-eye me-1'></i>View Only
        </span>
      </div>

      {/* ── Active Status Overview ── */}
      <div className='bg-light rounded-4 p-4 mb-4 border border-light-subtle'>
        <div className='d-flex align-items-start justify-content-between flex-wrap gap-3'>
          <div>
            <div className='d-flex align-items-center gap-2 mb-2'>
              <h5 className='fw-bold text-dark mb-0'>
                Status Aktif BMC Lembaga
              </h5>
              <BMCStatusBadge status={bmc.status} />
              <span className='badge bg-dark bg-opacity-10 text-dark rounded-pill px-2 py-1 extra-small fw-bold'>
                Versi {bmc.currentVersion}
              </span>
            </div>
            <div className='text-muted small mb-3'>
              <i className='bi bi-clock me-1 text-primary'></i> Update Terakhir:{' '}
              <strong className='text-dark'>{bmc.lastUpdate}</strong>
            </div>
            {bmc.feedback ? (
              <div
                className='bg-warning bg-opacity-10 text-warning-emphasis border border-warning border-opacity-25 rounded-3 p-3'
                style={{ fontSize: '0.8rem' }}
              >
                <div className='fw-bold mb-1'>
                  <i className='bi bi-chat-left-text-fill me-1'></i> Catatan
                  Revisi Admin:
                </div>
                <div className='lh-base'>{bmc.feedback}</div>
              </div>
            ) : (
              <div
                className='bg-success bg-opacity-10 text-success border border-success border-opacity-25 rounded-3 p-3'
                style={{ fontSize: '0.8rem' }}
              >
                <div className='fw-bold mb-1'>
                  <i className='bi bi-check-circle-fill me-1'></i> Status Aman:
                </div>
                <div className='lh-base'>
                  BMC telah disetujui atau siap dikembangkan lebih lanjut.
                </div>
              </div>
            )}
          </div>

          {/* Quick Metrics */}
          <div className='d-flex gap-3 flex-wrap align-items-center'>
            <div
              className='text-center bg-white px-3 py-2 rounded-3 border'
              style={{ minWidth: 100 }}
            >
              <div className='fs-4 fw-bold text-primary'>
                {bmc.currentVersion}
              </div>
              <div className='text-muted extra-small fw-semibold text-uppercase'>
                Total Versi
              </div>
            </div>
            <div
              className='text-center bg-white px-3 py-2 rounded-3 border'
              style={{ minWidth: 100 }}
            >
              <div className='fs-4 fw-bold text-success'>
                {totalContributors}
              </div>
              <div className='text-muted extra-small fw-semibold text-uppercase'>
                PO Kontributor
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Version Logs Header & Filters ── */}
      <div className='d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3 mb-3'>
        <div className='d-flex align-items-center gap-2'>
          <i className='bi bi-clock-history text-primary'></i>
          <h2 className='h6 fw-bold mb-0 text-dark'>
            Riwayat Perubahan & Kolaborasi Versi
          </h2>
        </div>
        <span className='text-muted small ms-auto'>
          {filteredHistory.length} dari {bmc.history.length} revisi
        </span>
      </div>

      {/* Filter Toolbar */}
      <div className='row g-3 mb-4 align-items-center'>
        <div className='col-md-6 col-lg-7'>
          <div className='input-group input-group-sm bg-light rounded-pill px-3 py-2 border-0 shadow-none'>
            <span className='input-group-text bg-transparent border-0 text-muted'>
              <i className='bi bi-search'></i>
            </span>
            <input
              type='text'
              className='form-control bg-transparent border-0 shadow-none'
              placeholder='Cari catatan revisi atau kontributor...'
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              style={{ fontSize: '0.85rem' }}
            />
          </div>
        </div>
        <div className='col-md-6 col-lg-5 d-flex gap-2 flex-wrap justify-content-md-end'>
          {[
            { key: 'all', label: 'Semua Status' },
            { key: 'Disetujui', label: 'Disetujui' },
            { key: 'Direvisi', label: 'Direvisi' },
            { key: 'Draft', label: 'Draft' },
          ].map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`btn btn-sm rounded-pill px-3 fw-medium ${filter === f.key ? 'btn-primary shadow-sm' : 'btn-light border border-light-subtle'}`}
              style={{ fontSize: '0.8rem' }}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Collaborative History Table ── */}
      <div className='table-responsive mb-4'>
        <table className='table table-hover align-middle mb-0'>
          <thead className='bg-light text-muted small text-uppercase fw-bold'>
            <tr>
              <th
                className='ps-4 py-3 border-0'
                style={{ width: 80 }}
              >
                Versi
              </th>
              <th className='border-0 py-3'>Kontributor (PO)</th>
              <th className='border-0 py-3'>Tanggal Update</th>
              <th className='border-0 py-3'>Catatan Pengajuan (Notes)</th>
              <th className='border-0 py-3 text-center'>Status</th>
              <th className='border-0 py-3 text-end pe-4'>
                Feedback / Catatan Admin
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredHistory.length > 0 ? (
              filteredHistory.map((h) => (
                <tr
                  key={h.version}
                  className='cursor-pointer transition-all'
                >
                  <td className='ps-4 py-3 fw-bold text-primary'>
                    v{h.version}
                  </td>
                  <td className='py-3'>
                    <div className='fw-bold text-dark'>{h.updatedBy}</div>
                  </td>
                  <td className='py-3'>
                    <div className='small text-muted d-flex align-items-center gap-1'>
                      <i className='bi bi-calendar3 text-primary opacity-50'></i>
                      {h.updatedAt}
                    </div>
                  </td>
                  <td
                    className='py-3'
                    style={{ maxWidth: 300 }}
                  >
                    <div
                      className='small text-dark text-truncate'
                      title={h.notes}
                    >
                      {h.notes}
                    </div>
                  </td>
                  <td className='py-3 text-center'>
                    <BMCStatusBadge status={h.status} />
                  </td>
                  <td
                    className='py-3 text-end pe-4'
                    style={{ maxWidth: 320 }}
                  >
                    {h.feedback ? (
                      <span
                        className='small text-muted fst-italic d-block text-end text-truncate'
                        title={h.feedback}
                      >
                        <i className='bi bi-chat-left-text text-warning me-1 opacity-75'></i>
                        {h.feedback}
                      </span>
                    ) : (
                      <span className='badge bg-success bg-opacity-10 text-success border border-success border-opacity-10 rounded-pill px-2 py-1 extra-small'>
                        <i className='bi bi-check2 me-1'></i>Aman
                      </span>
                    )}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={6}
                  className='text-center py-5'
                >
                  <div className='text-muted mb-3'>
                    <i className='bi bi-clock-history fs-1 opacity-25'></i>
                  </div>
                  <h6 className='fw-bold text-dark mb-1'>Tidak Ada Riwayat</h6>
                  <p className='text-muted small mb-0'>
                    Tidak ada versi BMC yang sesuai dengan filter Anda.
                  </p>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ── Educational Widget ── */}
      <div className='bg-primary bg-opacity-10 rounded-4 p-3 d-flex align-items-start gap-3 border border-primary border-opacity-25 shadow-none'>
        <i className='bi bi-info-circle-fill text-primary fs-4 mt-1'></i>
        <div style={{ fontSize: '0.8rem' }}>
          <h6 className='fw-bold text-primary mb-1'>
            Kolaborasi Bisnis: 1 BMC per Lembaga
          </h6>
          <p
            className='text-dark mb-0 opacity-75'
            style={{ lineHeight: '1.5' }}
          >
            Model pengerjaan BMC di CLP mengutamakan sinergi. Seluruh Project
            Officer (PO) yang membina **{DUMMY_BUMDES.name}** memiliki akses
            bersama ke lembar BMC yang sama. Pengisian dilakukan secara
            bergantian (gantian) dan komprehensif. DPL memantau perkembangan
            versi demi versi dan catatan revisi admin agar kualitas peta bisnis
            BUMDes terus meningkat hingga mencapai persetujuan final.
          </p>
        </div>
      </div>

      <style>{`
        .transition-all { transition: all 0.2s ease; }
        .cursor-pointer { cursor: pointer; }
        .extra-small { font-size: 0.72rem; }
        .table-hover tbody tr:hover { background-color: rgba(var(--bs-primary-rgb), 0.03) !important; transform: scale(0.998); }
      `}</style>
    </div>
  );
};

export default MonitoringBMC;
